import React, { useState } from "react";
import "./style.scss";

import { useFormik } from "formik";
import * as Yup from "yup";
import checkIcon from "../../../assets/icons/check-icon-blue.svg";
import eyeIcon from "../../../assets/icons/eye-icon-white.svg";
import eyeSlashedIcon from "../../../assets/icons/eye-slashed-icon-white.svg";
import googleLogo from "../../../assets/icons/google-icon-colored.svg";
import logoIcon from "../../../assets/icons/acg-logo.png";

import { Checkbox, FormControlLabel } from "@mui/material";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { setGoogleAuth, signin } from "../../../utils/store/reducers/authSlice";
import { returnErrors } from "../../../utils/store/reducers/Error";
import Spinner from "../../../reusableComponents/spinner/Spinner";
import TwoFactorAuthentication from "../2FA/TwoFactorAuthentication";

const SigninForm = () => {
  const [error, setError] = useState(false);
  const [tempToken, setTempToken] = useState({});
  const [isTwoFactorAuthVisible, setIsTwoFactorAuthVisible] = useState(false);
  const navigate = useNavigate();
  const inputs = {
    email: "",
    password: "",
  };

  const [rememberMe, setRememberMe] = useState(false);
  const schema = Yup.object().shape({
    email: Yup.string().email("Invalid email").required("Email is required"),
    password: Yup.string().required("Password is required"),
  });
  const dispatch = useDispatch();
  const location = useLocation();
  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    isSubmitting,
  } = useFormik({
    initialValues: inputs,
    validationSchema: schema,
    onSubmit: async (values, { setSubmitting, setErrors }) => {
      try {
        setSubmitting(true);
        const loginData = {
          email: values.email,
          password: values.password,
        };

        const res = await dispatch(signin({ loginData, rememberMe }));
        // Check if the response indicates 2FA is required
        if (res.payload && res.payload.data && res.payload.data.requires_2fa) {
          console.log("2FA required:", res.payload.data);
          // Store the temp token and show 2FA modal
          setTempToken(res.payload.data.temp_token);
          setIsTwoFactorAuthVisible(true);
          return; // Don't proceed with normal navigation
        }
        if (res.payload.status > 201) {
          const error = res.payload.data;
          let msg;
          if (Object.keys(error).length > 0) {
            const firstKey = Object.keys(error)[0];
            const firstString = error[firstKey] || error[firstKey][0];
            msg = firstString;
          } else {
            msg = "Something went wrong";
          }

          dispatch(returnErrors(msg, res.payload.status));
          setError(msg); // Set error message

          // Preserve field values and display error
          setErrors({ email: "", password: msg });

          // Prevent navigation on API failure
        } else {
          // Navigate only when API is successful
          const nextPath =
            new URLSearchParams(location.search).get("next") || "/";
          navigate(nextPath);
        }
      } catch (error) {
        console.error("Error occurred during login:", error);
      } finally {
        setSubmitting(false);
      }
    },
  });

  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isGoogleAuthLoading, setIsGoogleAuthLoading] = useState(false);
  // const liveUrl = "https://app.alpha-futures.com";
  const liveUrl = "https://app.alpha-futures.com";
  // const liveUrl = "http://localhost:3000";
  const handleGoogleAuth = async (e) => {
    e.preventDefault();
    setIsGoogleAuthLoading(true);

    try {
      const path = `https://accounts.google.com/o/oauth2/v2/auth?scope=https://www.googleapis.com/auth/userinfo.email&include_granted_scopes=true&response_type=code&state=pass-through value&redirect_uri=${liveUrl}/auth/google&client_id=889129287888-8dcqpkvg0e2mtk59vjr4mi5dedrj08ep.apps.googleusercontent.com`;

      // Create a promise that resolves after the redirection
      const googleAuthPromise = new Promise((resolve) => {
        window.location.replace(path);
        resolve();
      });

      // Await the completion of the promise
      await googleAuthPromise;
    } finally {
      // Set loading state to false when Google Auth finishes
      // setIsGoogleAuthLoading(false);
    }
  };

  return (
    <>
      {" "}
      <TwoFactorAuthentication
        visible={isTwoFactorAuthVisible}
        tempToken={tempToken}
        onCancel={() => {
          setIsTwoFactorAuthVisible(false);
          setTempToken("");
        }}
        onSuccess={(response) => {
          console.log("2FA verification successful:", response);
          dispatch(setGoogleAuth(response));
        }}
      />
      <div className="signinform-container">
        <div className="signinform-wrapper">
          <div className="company-logo">
            <img src={logoIcon} alt="company-logo" />
          </div>
          <div className="signin-form-info">
            <h2 className="form-header">Sign In</h2>
          </div>
          <form className="signinform-innnercontainer" onSubmit={handleSubmit}>
            <div className="signin-options" onClick={handleGoogleAuth}>
              {isGoogleAuthLoading ? (
                <Spinner />
              ) : (
                <>
                  <img src={googleLogo} alt="Google logo" />
                  <p>Continue with Google</p>
                </>
              )}
            </div>
            <div className="form-separator">
              <div className="left"></div>
              <p>Or log in using</p>
              <div className="left"></div>
            </div>
            <div
              className="signinform-subcontainer-form"
              style={{
                gap:
                  (touched.email && errors.email) ||
                  (touched.password && errors.password)
                    ? "20px"
                    : "30px",
              }}
            >
              <div className="input-wrapper">
                <p className="input-label">Email</p>
                <label
                  className={`${`signinform-container-input `}${
                    touched.email && errors.email && "form-empty-input"
                  }`}
                  htmlFor="email"
                >
                  <input
                    type="text"
                    className="signinform-input-box"
                    name="email"
                    value={values.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Your Email address"
                    id="email"
                  />
                  {!errors.email && touched.email && (
                    <img src={checkIcon} alt="Check icon" />
                  )}
                </label>
              </div>

              {touched.email && errors.email && (
                <div className="form-error-message">{errors.email}</div>
              )}

              <div className="input-wrapper">
                <p className="input-label">Password</p>
                <label
                  className={`${`signinform-container-input `}${
                    touched.password && errors.password && "form-empty-input"
                  }`}
                  htmlFor="password"
                >
                  <input
                    type={isPasswordVisible ? "text" : "password"}
                    className="signinform-input-box"
                    name="password"
                    value={values.password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Password"
                    id="password"
                  />
                  <img
                    style={{ cursor: "pointer" }}
                    src={isPasswordVisible ? eyeIcon : eyeSlashedIcon}
                    alt="Toggle password visibility"
                    onClick={() => setIsPasswordVisible((prev) => !prev)}
                  />
                </label>
              </div>
              {touched.password && errors.password && (
                <div className="form-error-message">{errors.password}</div>
              )}
            </div>
            <div className="signinform-submit-options">
              <div className="signinform-option">
                <div className="checkbox">
                  <FormControlLabel
                    control={<Checkbox id="rememberme" />}
                    onChange={(e) =>
                      setRememberMe(e.target.checked ? true : false)
                    }
                    sx={{
                      "& .MuiSvgIcon-root": {
                        color: "#fff",
                        borderRadius: "8px",
                      },
                      "&.Mui-checked": {
                        backgroundColor: "#01E1FF", // Background color when checked
                      },
                    }}
                  />
                  <label
                    htmlFor="rememberme"
                    style={{ color: "white", cursor: "pointer" }}
                  >
                    Remember me
                  </label>
                </div>
                <a className="forgot-password-link" href="/forgot-password">
                  Forgot Password?
                </a>
              </div>
              <button
                className="submit-button standard-button"
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? <Spinner /> : "Sign In"}
              </button>
              <p>
                Don't have an account yet?{" "}
                <span>
                  <a href="/signup">Sign up</a>
                </span>
              </p>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default SigninForm;
